import React, { useState, useMemo } from "react";
import { useMediaQuery } from "@material-ui/core";
import Hidden from "@material-ui/core/Hidden";
import { kebabCase } from "lodash";
import { StyledGrid, Button, ProgressBar } from "components/simple";
import { breakpoints, color, spacingDefaults } from "style/constants";
import { PolicySection, PolicyContent } from "./types";
import { PrivacyPolicyContent } from "./privacyPolicyContent";
import "./styles.css";

// Import styled components from Consent Form
import {
  Break,
  Ruler,
  SectionSubtitle,
  SectionBody,
  SectionList,
  Header
} from "components/pages/Consent/styled";

// Shared component for rendering policy content
const PolicyContentRenderer: React.FC<{
  policyContent: typeof PrivacyPolicyContent;
}> = React.memo(({ policyContent }) => (
  <StyledGrid padding="10px 0">
    {/* Header */}
    <SectionSubtitle id={kebabCase(policyContent.headers[0])}>
      {policyContent.headers[0]}
    </SectionSubtitle>
    {/* Overview */}
    <SectionBody>
      {policyContent.overview[0]}
      <strong>{policyContent.overview[1]}</strong>
      {policyContent.overview[2]}
    </SectionBody>
    <SectionBody>{policyContent.overview[3]}</SectionBody>

    {/* Policy Sections */}
    {policyContent.sections.map((section, index) => (
      <PolicySectionRenderer key={index} section={section} />
    ))}

    {/* Footer */}
    <SectionBody>
      {policyContent.footer.effectiveDate} <br />
      {policyContent.footer.lastUpdated} <br />
      <br />
      <i>{policyContent.footer.disclaimer}</i>
    </SectionBody>
  </StyledGrid>
));

// Component to render a policy section
const PolicySectionRenderer: React.FC<{ section: PolicySection }> = React.memo(
  ({ section }) => (
    <div className="policy-section">
      <SectionSubtitle id={`section-${kebabCase(section.title)}`}>
        {section.title}
      </SectionSubtitle>
      <div className="policy-content">
        {section.introduction && (
          <SectionBody>{section.introduction}</SectionBody>
        )}
        {section.items.map((item, idx) => (
          <PolicyItemRenderer key={idx} item={item} />
        ))}
      </div>
    </div>
  )
);

// Extracted PolicyItemRenderer component to improve readability and performance
const PolicyItemRenderer: React.FC<{ item: PolicyContent }> = React.memo(
  ({ item }) => {
    const indentClass = `indent-level-${item.indentLevel}`;

    return (
      <>
        <div className={indentClass}>
          <SectionBody>{item.content}</SectionBody>
        </div>
        {item.subitems && item.subitems.length > 0 && (
          <SectionList>
            {item.subitems.map((subitem, idx) => (
              <PolicyItemRenderer key={idx} item={subitem} />
            ))}
          </SectionList>
        )}
      </>
    );
  }
);

// SideNav component similar to the one in Consent Form
const SideNav: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [currentSection, setCurrentSection] = useState("privacy-policy");

  const handleSectionClick = (sectionId: string) => {
    setCurrentSection(sectionId);
    document.querySelector(`#${sectionId}`)?.scrollIntoView();
  };

  return (
    <StyledGrid
      backgroundColor={color.WHITE}
      border={`1px solid ${color.CARDGRAY}`}
      borderRadius="8px"
      container
      direction="column"
      item
      justifyContent="space-between"
      maxWidth="300px"
      minHeight="660px"
      padding="40px 30px"
      position="sticky"
      top="55px"
    >
      <StyledGrid item>
        <SectionSubtitle
          id="privacy-policy-nav"
          onClick={() => handleSectionClick("privacy-policy")}
          style={{ cursor: "pointer" }}
        >
          Privacy Policy
        </SectionSubtitle>
        <Ruler />
        {PrivacyPolicyContent.sections.map((section, index) => (
          <div key={index} style={{ marginBottom: "15px" }}>
            <SectionBody
              onClick={() =>
                handleSectionClick(`section-${kebabCase(section.title)}`)
              }
              style={{
                cursor: "pointer",
                fontWeight:
                  currentSection === `section-${kebabCase(section.title)}`
                    ? "bold"
                    : "normal",
                color:
                  currentSection === `section-${kebabCase(section.title)}`
                    ? color.DARKBLUE
                    : color.DARKERGRAY
              }}
            >
              {section.title}
            </SectionBody>
          </div>
        ))}
      </StyledGrid>
      <StyledGrid item>{children}</StyledGrid>
    </StyledGrid>
  );
};

const DocumentPage: React.FC<{}> = () => {
  const mobile = useMediaQuery(`(max-width:${breakpoints.lg}px)`);
  const policyContent = useMemo(() => PrivacyPolicyContent, []);

  const handleNextClick = (): void => {
    // This is a placeholder for any navigation logic you might want to add
    window.history.back();
  };

  return (
    <>
      <Hidden lgUp>
        <ProgressBar variant="steps" steps={1} activeStep={1} />
      </Hidden>
      <StyledGrid
        container
        direction={mobile ? "column" : "row"}
        justifyContent="flex-start"
        mobilePadding="0"
        padding="55px"
        minHeight="100vh"
        backgroundColor={mobile ? color.WHITE : undefined}
      >
        <Hidden mdDown>
          <StyledGrid item>
            <SideNav>
              <Button onClick={handleNextClick}>Back</Button>
            </SideNav>
          </StyledGrid>
        </Hidden>

        <StyledGrid item container height="100%" justifyContent="center" xs>
          <StyledGrid
            backgroundColor={color.WHITE}
            border={mobile ? "0" : `1px solid ${color.CARDGRAY}`}
            borderRadius="8px"
            container
            item
            maxWidth="955px"
            mobilePadding={`${spacingDefaults.xxlarge} ${spacingDefaults.normal}`}
            padding="90px 125px"
          >
            <article>
              <StyledGrid>
                <Header id="privacy-policy">Privacy Policy</Header>
              </StyledGrid>

              {!mobile && (
                <StyledGrid
                  item
                  margin="50px 0"
                  mobileMarginTop="25px"
                  mobileMarginBottom="25px"
                >
                  <Break>
                    <Ruler />
                  </Break>
                </StyledGrid>
              )}

              <PolicyContentRenderer policyContent={policyContent} />

              <StyledGrid
                container
                direction="column"
                alignItems="center"
                marginTop="2rem"
              >
                <Button
                  stickyFooter={mobile}
                  onClick={handleNextClick}
                  width={mobile ? undefined : "300px"}
                  zIndex={1000}
                >
                  Back
                </Button>
              </StyledGrid>
            </article>
          </StyledGrid>
        </StyledGrid>
      </StyledGrid>
    </>
  );
};

export default DocumentPage;
